import { useCallback } from 'react';

import merge from 'lodash/merge';

import useAuthToken from './useAuthToken';

/**
 * Returns a wrapper around the `fetch` function that handles JWT-based authentication.
 * Note that the fetch is only authorized with "invoke-model" scope. If the first attempt
 * to fetch fails with a 403 or 401, the auth token is refreshed and the fetch is retried.
 */
export default function useFetchWithAuthToken(
  isSharedPlayground = false,
  isSharedEndpoint = false,
): typeof fetch {
  const [authToken, refreshAuthToken] = useAuthToken(true, isSharedPlayground, isSharedEndpoint);

  return useCallback(
    async (input: RequestInfo, init?: RequestInit) => {
      let latestAuthToken = authToken;

      if (!latestAuthToken || isSharedPlayground) {
        latestAuthToken = await refreshAuthToken();
      }

      for (let retriesLeft = 1; retriesLeft >= 0; retriesLeft -= 1) {
        const resp = await fetch(
          input,
          merge({}, init, {
            headers: {
              Authorization: `Bearer ${latestAuthToken}`,
              'Content-Type': 'application/json',
            },
            // Don't include cookies in the request
            credentials: 'omit',
          }),
        );

        if ((resp.status === 403 || resp.status === 401) && retriesLeft > 0) {
          latestAuthToken = await refreshAuthToken();
        } else {
          return resp;
        }
      }
    },
    [authToken, refreshAuthToken, isSharedPlayground],
  );
}
